// Promociones / Happy Hour — CRUD + helper para aplicar precios al POS.
//
// Una promoción "aplica" a un producto cuando:
//   - está activa (activo = true)
//   - estamos dentro de la franja horaria (hora_inicio - hora_fin)
//   - el día de hoy está en dias_semana
//   - estamos en la vigencia (vigencia_desde - vigencia_hasta)
//   - producto_id coincide (o producto_id es NULL = aplica a todos)

import { restGet, restWrite, one } from './supabaseRest';
import { getBusinessId, USE_MOCK } from './supabaseClient';

// Demo pública / mock: promos en memoria. Incluye motivos "a mano" (0062)
// para que el POS de la demo muestre los chips de descuento.
const PROMOS_MOCK = [
  { id: 'promo-m1', nombre: 'Descuento empleado',      producto_id: null, tipo: 'descuento_pct', porcentaje: 20,  manual: true, activo: true, hora_inicio: '00:00:00', hora_fin: '23:59:00', dias_semana: [1,2,3,4,5,6,7], created_at: '2026-09-01T12:00:00Z' },
  { id: 'promo-m2', nombre: 'Resarcimiento (demora)',  producto_id: null, tipo: 'descuento_pct', porcentaje: 100, manual: true, activo: true, hora_inicio: '00:00:00', hora_fin: '23:59:00', dias_semana: [1,2,3,4,5,6,7], created_at: '2026-09-01T12:00:00Z' },
  { id: 'promo-m3', nombre: 'Fin de tortas',           producto_id: null, tipo: 'descuento_pct', porcentaje: 30,  manual: true, activo: true, hora_inicio: '00:00:00', hora_fin: '23:59:00', dias_semana: [1,2,3,4,5,6,7], created_at: '2026-09-01T12:00:00Z' },
];

function bid() {
  const id = getBusinessId();
  if (!id) throw new Error('no_business');
  return id;
}

function fromPromo(r) {
  if (!r) return null;
  return {
    id:           r.id,
    nombre:       r.nombre,
    productoId:   r.producto_id,
    tipo:         r.tipo,
    precioPromo:  r.precio_promo != null ? Number(r.precio_promo) : null,
    porcentaje:   r.porcentaje,
    montoCop:     r.monto_cop != null ? Number(r.monto_cop) : null,
    horaInicio:   r.hora_inicio,  // "15:00:00"
    horaFin:      r.hora_fin,
    diasSemana:   r.dias_semana ?? [1,2,3,4,5,6,7],
    vigenciaDesde: r.vigencia_desde,
    vigenciaHasta: r.vigencia_hasta,
    activo:       r.activo,
    // 0062: true = motivo de descuento que el cajero aplica a mano en el POS
    // (empleado, resarcimiento, fin de tortas…). NO se aplica sola por horario.
    manual:       !!r.manual,
    createdAt:    r.created_at,
  };
}

function toPromoRow(p) {
  return {
    nombre:           p.nombre,
    producto_id:      p.productoId ?? null,
    tipo:             p.tipo,
    manual:           !!p.manual,
    precio_promo:     p.precioPromo ?? null,
    porcentaje:       p.porcentaje ?? null,
    monto_cop:        p.montoCop ?? null,
    hora_inicio:      p.horaInicio,
    hora_fin:         p.horaFin,
    dias_semana:      p.diasSemana ?? [1,2,3,4,5,6,7],
    vigencia_desde:   p.vigenciaDesde ?? null,
    vigencia_hasta:   p.vigenciaHasta ?? null,
    activo:           p.activo ?? true,
  };
}

export async function listarPromociones() {
  if (USE_MOCK) return PROMOS_MOCK.map(fromPromo);
  try {
    const rows = await restGet(
      `/promociones?business_id=eq.${bid()}&select=*&order=created_at.desc`
    );
    return (rows ?? []).map(fromPromo);
  } catch {
    return [];
  }
}

export async function crearPromocion(payload) {
  if (USE_MOCK) {
    const r = { ...toPromoRow(payload), id: `promo-${Date.now().toString(36)}`, created_at: new Date().toISOString() };
    PROMOS_MOCK.unshift(r);
    return fromPromo(r);
  }
  const row = { ...toPromoRow(payload), business_id: bid() };
  return fromPromo(one(await restWrite('POST', '/promociones', row)));
}

export async function actualizarPromocion(id, payload) {
  if (USE_MOCK) { const i = PROMOS_MOCK.findIndex((p) => p.id === id); if (i >= 0) PROMOS_MOCK[i] = { ...PROMOS_MOCK[i], ...toPromoRow(payload) }; return fromPromo(PROMOS_MOCK[i]); }
  const row = toPromoRow(payload);
  return fromPromo(one(await restWrite('PATCH',
    `/promociones?id=eq.${id}&business_id=eq.${bid()}`, row)));
}

export async function eliminarPromocion(id) {
  if (USE_MOCK) { const i = PROMOS_MOCK.findIndex((p) => p.id === id); if (i >= 0) PROMOS_MOCK.splice(i, 1); return; }
  await restWrite('DELETE',
    `/promociones?id=eq.${id}&business_id=eq.${bid()}`, null, 'return=minimal');
}

export async function togglePromocion(id, activo) {
  if (USE_MOCK) { const r = PROMOS_MOCK.find((p) => p.id === id); if (r) r.activo = activo; return; }
  await restWrite('PATCH',
    `/promociones?id=eq.${id}&business_id=eq.${bid()}`,
    { activo }, 'return=minimal');
}

// ─── Helper: ¿qué precio aplica AHORA para este producto? ───────────────────
//
// Recibe las promociones cargadas + el producto. Retorna:
//   { precio: COP, promo: <objeto promo> | null }
// Si no hay promo activa, retorna { precio: producto.precio, promo: null }
//
// Se llama desde el POS al armar el carrito. Mantenerlo PURO (sin estado)
// para que sea fácil testear.

export function precioConPromo(producto, promociones, ahora = new Date()) {
  if (!producto || !promociones || promociones.length === 0) {
    return { precio: producto?.precio ?? 0, promo: null };
  }
  const precioBase = Number(producto.precio) || 0;

  // Día de la semana (1=lun, 7=dom). JS getDay() es 0=dom..6=sab
  const diaJs = ahora.getDay();
  const diaSem = diaJs === 0 ? 7 : diaJs;
  const horaActual = `${String(ahora.getHours()).padStart(2, '0')}:${String(ahora.getMinutes()).padStart(2, '0')}`;
  const hoy = ahora.toISOString().split('T')[0];

  // Filtrar promos que apliquen AHORA
  const aplicables = promociones.filter((p) => {
    if (!p.activo) return false;
    if (p.manual) return false;   // a mano desde el carrito, nunca sola
    if (p.productoId && p.productoId !== producto.id) return false;
    if (!p.diasSemana?.includes(diaSem)) return false;
    if (p.vigenciaDesde && hoy < p.vigenciaDesde) return false;
    if (p.vigenciaHasta && hoy > p.vigenciaHasta) return false;
    // Hora — comparamos en formato HH:MM
    const ini = (p.horaInicio ?? '00:00').slice(0, 5);
    const fin = (p.horaFin    ?? '23:59').slice(0, 5);
    // Soporte para franjas que cruzan medianoche (ej. 22:00 - 02:00)
    if (ini <= fin) {
      return horaActual >= ini && horaActual <= fin;
    }
    return horaActual >= ini || horaActual <= fin;
  });

  if (aplicables.length === 0) return { precio: precioBase, promo: null };

  // Si hay varias, aplicamos la que da el MEJOR descuento al cliente
  let mejor = { precio: precioBase, promo: null };
  for (const p of aplicables) {
    let precio = precioBase;
    if (p.tipo === 'precio_fijo' && p.precioPromo != null) {
      precio = Number(p.precioPromo);
    } else if (p.tipo === 'descuento_pct' && p.porcentaje != null) {
      precio = Math.round(precioBase * (100 - p.porcentaje) / 100 / 100) * 100;
    } else if (p.tipo === 'descuento_cop' && p.montoCop != null) {
      precio = Math.max(0, precioBase - Number(p.montoCop));
    }
    if (precio < mejor.precio) {
      mejor = { precio, promo: p };
    }
  }

  return mejor;
}

export const promocionesService = {
  listarPromociones,
  crearPromocion,
  actualizarPromocion,
  eliminarPromocion,
  togglePromocion,
  precioConPromo,
};

export default promocionesService;
